/**
 * ReHub v2 commercial catalogue — new-customer plans and PAYG packs.
 *
 * v1 definitions in credit-packs.ts stay authoritative for existing Stripe
 * Price IDs and grandfathered subscribers. Never change what a v1 price grants.
 *
 * Amounts are integer minor units (pence / cents). No FX conversion.
 */

import { minorToMajor, REHUB_CURRENCIES } from './currency';
import { PROPOSED_EUR_CREDIT_MONTHLY_V2, PROPOSED_EUR_CREDIT_PACKS_V2 } from './proposed-eur';
import {
  formatPerCreditFromMinor,
  getPackStripePriceId,
  getSubscriptionStripePriceId,
  KNOWN_SUBSCRIPTION_PRICE_TO_PLAN,
  REHUB_CREDIT_MONTHLY_PLANS,
  REHUB_CREDIT_PACKS,
  getCreditPack,
  getCreditSubscription,
  type CreditCurrency,
  type RehubCreditPackDefinition,
  type RehubCreditSubscriptionDefinition,
} from './credit-packs';

export const REHUB_PRICING_VERSION = 'v2' as const;
export type RehubPricingVersion = 'v1' | 'v2';

export type BillingInterval = 'month' | 'year';

export type RehubCreditPackIdV2 =
  | 'credits_20'
  | 'credits_50_v2'
  | 'credits_150_v2'
  | 'credits_500';

/**
 * Monthly plan ids. `*_v2` are the original v2-wallet plans; `pro_v3` /
 * `studio_v3` are the current catalogue revision (same prices, reduced
 * allowances: Pro 130, Studio 350). Starter is unchanged so `starter_v2`
 * remains the live Starter plan. `pro_v2` (150) and `studio_v2` (500) are
 * grandfathered: their Stripe Price IDs must keep granting the original
 * allowances to existing subscribers forever.
 */
export type RehubCreditSubscriptionIdV2 =
  | 'starter_v2'
  | 'pro_v2'
  | 'studio_v2'
  | 'pro_v3'
  | 'studio_v3';

export type RehubCreditType =
  | 'subscription'
  | 'purchase'
  | 'bonus'
  | 'refund'
  | 'admin'
  | 'expired';

export interface RehubCreditPackDefinitionV2 {
  id: RehubCreditPackIdV2;
  pricingVersion: 'v2';
  credits: number;
  label: string;
  ctaLabel: string;
  amountMinor: Record<CreditCurrency, number>;
  perCredit: Record<CreditCurrency, string>;
  sortOrder: number;
  badge?: 'popular' | 'high-volume';
  neverExpires: true;
  /** Live v2 Price IDs. Env vars override these. */
  defaultStripePriceIds: Partial<Record<CreditCurrency, string>>;
}

export interface RehubCreditSubscriptionDefinitionV2 {
  id: RehubCreditSubscriptionIdV2;
  pricingVersion: 'v2';
  displayId: 'starter' | 'pro' | 'studio';
  credits: number;
  label: string;
  amountMinor: Record<CreditCurrency, number>;
  perCredit: Record<CreditCurrency, string>;
  sortOrder: number;
  badge?: 'popular';
  interval: BillingInterval;
  rolloverCap: number;
  includedUsers: number;
  features: readonly string[];
  /** Live v2 Price IDs. Env vars override these. */
  defaultStripePriceIds: Partial<Record<CreditCurrency, string>>;
}

export interface RehubEnterprisePlanDefinition {
  id: 'enterprise_v2';
  pricingVersion: 'v2';
  displayId: 'enterprise';
  label: 'Enterprise';
  credits: null;
  amountMinor: null;
  includedUsers: null;
  cta: 'contact_sales';
  features: readonly string[];
}

/**
 * USD = GBP × 1.25 rounded up to the whole dollar, EUR = GBP × 1.20. Every Price ID here must charge exactly
 * `amountMinor`. A missing ID fails that checkout; never fill it with a Price
 * at a different amount. Retired (old-amount) Price IDs are listed in
 * `RETIRED_TOPUP_PRICE_IDS` and must not be reused.
 */
export const REHUB_CREDIT_PACKS_V2: readonly RehubCreditPackDefinitionV2[] = [
  {
    id: 'credits_20',
    pricingVersion: 'v2',
    credits: 20,
    label: '20 Credits',
    ctaLabel: 'Buy 20 credits',
    amountMinor: { GBP: 1500, EUR: PROPOSED_EUR_CREDIT_PACKS_V2.credits_20.amountMinor, USD: 1900 },
    perCredit: { GBP: '£0.75', EUR: PROPOSED_EUR_CREDIT_PACKS_V2.credits_20.perCredit, USD: '$0.95' },
    sortOrder: 10,
    neverExpires: true,
    defaultStripePriceIds: {
      GBP: 'price_1UD4H3DHlg75FZHHJSfjDhSl',
      EUR: 'price_1UKau1DHlg75FZHHt7BlsBkR',
      USD: 'price_1UKazEDHlg75FZHH0Bu2HeFV',
    },
  },
  {
    id: 'credits_50_v2',
    pricingVersion: 'v2',
    credits: 50,
    label: '50 Credits',
    ctaLabel: 'Buy 50 credits',
    amountMinor: { GBP: 3500, EUR: PROPOSED_EUR_CREDIT_PACKS_V2.credits_50_v2.amountMinor, USD: 4400 },
    perCredit: { GBP: '£0.70', EUR: PROPOSED_EUR_CREDIT_PACKS_V2.credits_50_v2.perCredit, USD: '$0.88' },
    sortOrder: 20,
    badge: 'popular',
    neverExpires: true,
    defaultStripePriceIds: {
      GBP: 'price_1UD4H6DHlg75FZHHpprdahVX',
      EUR: 'price_1UESl7DHlg75FZHHCDgRVR8W',
      USD: 'price_1UKazEDHlg75FZHHXYOARu6a',
    },
  },
  {
    id: 'credits_150_v2',
    pricingVersion: 'v2',
    credits: 150,
    label: '150 Credits',
    ctaLabel: 'Buy 150 credits',
    amountMinor: { GBP: 9000, EUR: PROPOSED_EUR_CREDIT_PACKS_V2.credits_150_v2.amountMinor, USD: 11300 },
    perCredit: { GBP: '£0.60', EUR: PROPOSED_EUR_CREDIT_PACKS_V2.credits_150_v2.perCredit, USD: '$0.75' },
    sortOrder: 30,
    badge: 'high-volume',
    neverExpires: true,
    defaultStripePriceIds: {
      GBP: 'price_1UD4H8DHlg75FZHHNssyZX3V',
      EUR: 'price_1UKau4DHlg75FZHHibS538N0',
      USD: 'price_1UKazEDHlg75FZHHKUkJtS2J',
    },
  },
  {
    id: 'credits_500',
    pricingVersion: 'v2',
    credits: 500,
    label: '500 Credits',
    ctaLabel: 'Buy 500 credits',
    amountMinor: { GBP: 25000, EUR: PROPOSED_EUR_CREDIT_PACKS_V2.credits_500.amountMinor, USD: 31300 },
    perCredit: { GBP: '£0.50', EUR: PROPOSED_EUR_CREDIT_PACKS_V2.credits_500.perCredit, USD: '$0.63' },
    sortOrder: 40,
    badge: 'high-volume',
    neverExpires: true,
    defaultStripePriceIds: {
      GBP: 'price_1UD4HADHlg75FZHHhcrVqAh5',
      EUR: 'price_1UKau6DHlg75FZHHJdeKQVdA',
      USD: 'price_1UKazEDHlg75FZHHK1WGTceh',
    },
  },
] as const;

/** Live top-up Prices that still charge superseded EUR/USD amounts. */
export const RETIRED_TOPUP_PRICE_IDS: readonly string[] = [
  'price_1UESl5DHlg75FZHHreYIYUKe', // 20 EUR €19
  'price_1UD4H5DHlg75FZHHbGVbd6lI', // 20 USD $20
  'price_1UD4H7DHlg75FZHHj5i6HWDg', // 50 USD $45
  'price_1UESl9DHlg75FZHHocSaIN5F', // 150 EUR €109
  'price_1UD4H9DHlg75FZHHDivvh4YC', // 150 USD $119
  'price_1UESl9DHlg75FZHHDQhm7vqb', // 500 EUR €299
  'price_1UD4HBDHlg75FZHHX2dobxpA', // 500 USD $329
  'price_1UIp6hDHlg75FZHHQkqzJCQi', // 20 EUR €19 (v3)
  'price_1UIp6iDHlg75FZHH3NDJR9SJ', // 20 USD $21 (v3)
  'price_1UIp6jDHlg75FZHHIgbQxXoV', // 50 EUR €45 (v3)
  'price_1UIp6kDHlg75FZHHVarkTmVF', // 50 USD $49 (v3)
  'price_1UIp6lDHlg75FZHHKq6n7FWl', // 150 EUR €109 (v3)
  'price_1UIp6lDHlg75FZHH3Q7QEkyr', // 150 USD $125 (v3)
  'price_1UIp6mDHlg75FZHHEnMyhxWM', // 500 EUR €299 (v3)
  'price_1UIp6nDHlg75FZHHPe2m8AYZ', // 500 USD $349 (v3)
];

const STARTER_V2_PLAN: RehubCreditSubscriptionDefinitionV2 = {
  id: 'starter_v2',
  pricingVersion: 'v2',
  displayId: 'starter',
  credits: 40,
  label: 'Starter',
  amountMinor: { GBP: 1900, EUR: PROPOSED_EUR_CREDIT_MONTHLY_V2.starter_v2.amountMinor, USD: 2500 },
  perCredit: { GBP: '£0.48', EUR: PROPOSED_EUR_CREDIT_MONTHLY_V2.starter_v2.perCredit, USD: '$0.63' },
  sortOrder: 10,
  interval: 'month',
  rolloverCap: 120,
  includedUsers: 1,
  features: [
    'All eligible ReHub AI tools',
    'Shared ReHub credit wallet',
    'Credit rollover',
    'PAYG top-ups',
    'Standard processing',
  ],
  defaultStripePriceIds: {
    GBP: 'price_1UD4GxDHlg75FZHHg1L36KbC',
    EUR: 'price_1UESlHDHlg75FZHHu9qR2BSz',
    USD: 'price_1UD4GyDHlg75FZHHJ7xV4Vy0',
  },
};

/**
 * Grandfathered Pro (150/mo). Existing subscribers keep this allowance.
 * Never change credits, rolloverCap, or Stripe Price IDs on this definition.
 */
const PRO_V2_PLAN_GRANDFATHERED: RehubCreditSubscriptionDefinitionV2 = {
  id: 'pro_v2',
  pricingVersion: 'v2',
  displayId: 'pro',
  credits: 150,
  label: 'Pro',
  amountMinor: { GBP: 5900, EUR: PROPOSED_EUR_CREDIT_MONTHLY_V2.pro_v2.amountMinor, USD: 7900 },
  perCredit: { GBP: '£0.39', EUR: PROPOSED_EUR_CREDIT_MONTHLY_V2.pro_v2.perCredit, USD: '$0.53' },
  sortOrder: 20,
  badge: 'popular',
  interval: 'month',
  rolloverCap: 450,
  includedUsers: 3,
  features: [
    'All eligible ReHub AI tools',
    'Shared wallet',
    'Credit rollover',
    'PAYG top-ups',
    'Priority processing where supported',
    'Team access for up to 3 users',
  ],
  defaultStripePriceIds: {
    GBP: 'price_1UD4GzDHlg75FZHHgg1BdVJi',
    EUR: 'price_1UESlIDHlg75FZHHBeVSKDIh',
    USD: 'price_1UD4H0DHlg75FZHHzXS6h1Ov',
  },
};

/** Current Pro plan (130/mo, same price as pro_v2). */
const PRO_V3_PLAN: RehubCreditSubscriptionDefinitionV2 = {
  id: 'pro_v3',
  pricingVersion: 'v2',
  displayId: 'pro',
  credits: 130,
  label: 'Pro',
  amountMinor: { GBP: 5900, EUR: PROPOSED_EUR_CREDIT_MONTHLY_V2.pro_v3.amountMinor, USD: 7900 },
  perCredit: { GBP: '£0.45', EUR: PROPOSED_EUR_CREDIT_MONTHLY_V2.pro_v3.perCredit, USD: '$0.61' },
  sortOrder: 20,
  badge: 'popular',
  interval: 'month',
  rolloverCap: 390,
  includedUsers: 3,
  features: [
    'All eligible ReHub AI tools',
    'Shared wallet',
    'Credit rollover',
    'PAYG top-ups',
    'Priority processing where supported',
    'Team access for up to 3 users',
  ],
  defaultStripePriceIds: {
    GBP: 'price_1UGYN1DHlg75FZHHQfI4YwEB',
    EUR: 'price_1UGYN2DHlg75FZHH5eKmream',
    USD: 'price_1UGYN3DHlg75FZHHgDpKLFVd',
  },
};

/**
 * Grandfathered Studio (500/mo). Existing subscribers keep this allowance.
 * Never change credits, rolloverCap, or Stripe Price IDs on this definition.
 */
const STUDIO_V2_PLAN_GRANDFATHERED: RehubCreditSubscriptionDefinitionV2 = {
  id: 'studio_v2',
  pricingVersion: 'v2',
  displayId: 'studio',
  credits: 500,
  label: 'Studio',
  amountMinor: { GBP: 14900, EUR: PROPOSED_EUR_CREDIT_MONTHLY_V2.studio_v2.amountMinor, USD: 19900 },
  perCredit: { GBP: '£0.30', EUR: PROPOSED_EUR_CREDIT_MONTHLY_V2.studio_v2.perCredit, USD: '$0.40' },
  sortOrder: 30,
  interval: 'month',
  rolloverCap: 1500,
  includedUsers: 10,
  features: [
    'All eligible ReHub AI tools',
    'Shared wallet',
    'Credit rollover',
    'PAYG top-ups',
    'Priority processing',
    'Higher batch limits',
    'Up to 10 team members',
    'Studio/agency usage',
  ],
  defaultStripePriceIds: {
    GBP: 'price_1UD4H2DHlg75FZHHY5ElRqfz',
    EUR: 'price_1UESlKDHlg75FZHH6hMnjoB6',
    USD: 'price_1UD4H3DHlg75FZHHPbezY1M0',
  },
};

/** Current Studio plan (350/mo, same price as studio_v2). */
const STUDIO_V3_PLAN: RehubCreditSubscriptionDefinitionV2 = {
  id: 'studio_v3',
  pricingVersion: 'v2',
  displayId: 'studio',
  credits: 350,
  label: 'Studio',
  amountMinor: { GBP: 14900, EUR: PROPOSED_EUR_CREDIT_MONTHLY_V2.studio_v3.amountMinor, USD: 19900 },
  perCredit: { GBP: '£0.43', EUR: PROPOSED_EUR_CREDIT_MONTHLY_V2.studio_v3.perCredit, USD: '$0.57' },
  sortOrder: 30,
  interval: 'month',
  rolloverCap: 1050,
  includedUsers: 10,
  features: [
    'All eligible ReHub AI tools',
    'Shared wallet',
    'Credit rollover',
    'PAYG top-ups',
    'Priority processing',
    'Higher batch limits',
    'Up to 10 team members',
    'Studio/agency usage',
  ],
  defaultStripePriceIds: {
    GBP: 'price_1UGYN5DHlg75FZHHLJHBV0sx',
    EUR: 'price_1UGYN6DHlg75FZHHSknTRWuj',
    USD: 'price_1UGYN7DHlg75FZHH9OMmrxl3',
  },
};

/**
 * Current new-customer monthly catalogue (v3 revision): Starter 40 / Pro 130 /
 * Studio 350. All new subscriptions and marketing/pricing displays use this.
 */
export const REHUB_CREDIT_MONTHLY_PLANS_V2: readonly RehubCreditSubscriptionDefinitionV2[] = [
  STARTER_V2_PLAN,
  PRO_V3_PLAN,
  STUDIO_V3_PLAN,
] as const;

/**
 * Grandfathered v2 plans (Pro 150 / Studio 500). Not sold any more, but their
 * plan ids and Stripe Price IDs must keep resolving to the original
 * allowances so existing subscribers renew on their agreed terms.
 */
export const REHUB_GRANDFATHERED_CREDIT_MONTHLY_PLANS_V2: readonly RehubCreditSubscriptionDefinitionV2[] = [
  PRO_V2_PLAN_GRANDFATHERED,
  STUDIO_V2_PLAN_GRANDFATHERED,
] as const;

/**
 * Every v2-wallet plan definition (grandfathered first, current last) for
 * plan-id and price-id resolution.
 */
const ALL_V2_WALLET_PLANS: readonly RehubCreditSubscriptionDefinitionV2[] = [
  ...REHUB_GRANDFATHERED_CREDIT_MONTHLY_PLANS_V2,
  ...REHUB_CREDIT_MONTHLY_PLANS_V2,
] as const;

export const REHUB_ENTERPRISE_PLAN_V2: RehubEnterprisePlanDefinition = {
  id: 'enterprise_v2',
  pricingVersion: 'v2',
  displayId: 'enterprise',
  label: 'Enterprise',
  credits: null,
  amountMinor: null,
  includedUsers: null,
  cta: 'contact_sales',
  features: [
    'Everything in Studio',
    'High-volume usage',
    'Custom credit allocation',
    'API access',
    'Custom team limits',
    'Custom billing',
    'Priority support',
    'Bespoke commercial agreements',
  ],
};

export const REHUB_FREE_PLAN_V2 = {
  id: 'free_v2',
  pricingVersion: 'v2' as const,
  displayId: 'free' as const,
  label: 'Free',
  credits: 10,
  creditsOnce: true,
  amountMinor: { GBP: 0, EUR: 0, USD: 0 } as Record<CreditCurrency, number>,
  includedUsers: 1,
  features: [
    'Access to eligible ReHub AI tools',
    '10 ReHub Credits once',
    '10 HDR Credits / month for HDRPhoto',
    'PAYG ReHub Credit top-ups',
    'No card required',
  ] as const,
};

export const V2_PACK_ENV_KEYS: Record<RehubCreditPackIdV2, Record<CreditCurrency, string>> = {
  credits_20: { GBP: 'VITE_STRIPE_PRICE_CREDITS_20_GBP', EUR: 'VITE_STRIPE_PRICE_CREDITS_20_EUR', USD: 'VITE_STRIPE_PRICE_CREDITS_20_USD' },
  credits_50_v2: { GBP: 'VITE_STRIPE_PRICE_CREDITS_50_V2_GBP', EUR: 'VITE_STRIPE_PRICE_CREDITS_50_V2_EUR', USD: 'VITE_STRIPE_PRICE_CREDITS_50_V2_USD' },
  credits_150_v2: { GBP: 'VITE_STRIPE_PRICE_CREDITS_150_V2_GBP', EUR: 'VITE_STRIPE_PRICE_CREDITS_150_V2_EUR', USD: 'VITE_STRIPE_PRICE_CREDITS_150_V2_USD' },
  credits_500: { GBP: 'VITE_STRIPE_PRICE_CREDITS_500_GBP', EUR: 'VITE_STRIPE_PRICE_CREDITS_500_EUR', USD: 'VITE_STRIPE_PRICE_CREDITS_500_USD' },
};

export const V2_SUB_ENV_KEYS: Record<RehubCreditSubscriptionIdV2, Record<CreditCurrency, string>> = {
  starter_v2: { GBP: 'VITE_STRIPE_PRICE_SUB_STARTER_V2_GBP', EUR: 'VITE_STRIPE_PRICE_SUB_STARTER_V2_EUR', USD: 'VITE_STRIPE_PRICE_SUB_STARTER_V2_USD' },
  pro_v2: { GBP: 'VITE_STRIPE_PRICE_SUB_PRO_V2_GBP', EUR: 'VITE_STRIPE_PRICE_SUB_PRO_V2_EUR', USD: 'VITE_STRIPE_PRICE_SUB_PRO_V2_USD' },
  studio_v2: { GBP: 'VITE_STRIPE_PRICE_SUB_STUDIO_V2_GBP', EUR: 'VITE_STRIPE_PRICE_SUB_STUDIO_V2_EUR', USD: 'VITE_STRIPE_PRICE_SUB_STUDIO_V2_USD' },
  pro_v3: { GBP: 'VITE_STRIPE_PRICE_SUB_PRO_V3_GBP', EUR: 'VITE_STRIPE_PRICE_SUB_PRO_V3_EUR', USD: 'VITE_STRIPE_PRICE_SUB_PRO_V3_USD' },
  studio_v3: { GBP: 'VITE_STRIPE_PRICE_SUB_STUDIO_V3_GBP', EUR: 'VITE_STRIPE_PRICE_SUB_STUDIO_V3_EUR', USD: 'VITE_STRIPE_PRICE_SUB_STUDIO_V3_USD' },
};

export const V2_STRIPE_ENV_KEYS = {
  packs: V2_PACK_ENV_KEYS,
  subscriptions: V2_SUB_ENV_KEYS,
} as const;

type StripePriceEnv = Partial<Record<string, string | undefined>>;

const PACK_V2_ALIASES: Record<string, RehubCreditPackIdV2> = {
  credits_20: 'credits_20',
  pack_20: 'credits_20',
  credits_50_v2: 'credits_50_v2',
  credits_150_v2: 'credits_150_v2',
  credits_500: 'credits_500',
  pack_500: 'credits_500',
};

const PLAN_V2_ALIASES: Record<string, RehubCreditSubscriptionIdV2> = {
  starter_v2: 'starter_v2',
  pro_v2: 'pro_v2',
  studio_v2: 'studio_v2',
  pro_v3: 'pro_v3',
  studio_v3: 'studio_v3',
};

export function isV2PackId(id: string | null | undefined): id is RehubCreditPackIdV2 {
  return !!id && id in PACK_V2_ALIASES;
}

export function isV2PlanId(id: string | null | undefined): id is RehubCreditSubscriptionIdV2 {
  return !!id && id in PLAN_V2_ALIASES;
}

export function getCreditPackV2(id: string | null | undefined): RehubCreditPackDefinitionV2 | null {
  if (!id) return null;
  const normalized = PACK_V2_ALIASES[id];
  if (!normalized) return null;
  return REHUB_CREDIT_PACKS_V2.find((p) => p.id === normalized) ?? null;
}

export function getCreditSubscriptionV2(
  id: string | null | undefined
): RehubCreditSubscriptionDefinitionV2 | null {
  if (!id) return null;
  const normalized = PLAN_V2_ALIASES[id];
  if (!normalized) return null;
  // Searches current + grandfathered plans so `pro_v2` still resolves to 150.
  return ALL_V2_WALLET_PLANS.find((p) => p.id === normalized) ?? null;
}

/** Resolve a pack from either catalogue. v2 IDs win when both could match. */
export function getAnyCreditPack(
  id: string | null | undefined
): RehubCreditPackDefinition | RehubCreditPackDefinitionV2 | null {
  return getCreditPackV2(id) || getCreditPack(id);
}

/** Resolve a subscription from either catalogue. v2 IDs (`starter_v2`) never collide with v1 (`starter`). */
export function getAnyCreditSubscription(
  id: string | null | undefined
): RehubCreditSubscriptionDefinition | RehubCreditSubscriptionDefinitionV2 | null {
  return getCreditSubscriptionV2(id) || getCreditSubscription(id);
}

function envLookup(env: StripePriceEnv, keys: string[]): string {
  for (const key of keys) {
    const value = env[key];
    if (value) return value;
  }
  return '';
}

export function getPackStripePriceIdV2(
  pack: RehubCreditPackDefinitionV2,
  currency: CreditCurrency,
  env: StripePriceEnv = {}
): string {
  const viteKey = V2_PACK_ENV_KEYS[pack.id][currency];
  const serverKey = viteKey.replace(/^VITE_/, '');
  const fromEnv = envLookup(env, [viteKey, serverKey]);
  if (fromEnv && !RETIRED_TOPUP_PRICE_IDS.includes(fromEnv)) return fromEnv;
  return pack.defaultStripePriceIds[currency] || '';
}

export function getSubscriptionStripePriceIdV2(
  plan: RehubCreditSubscriptionDefinitionV2,
  currency: CreditCurrency,
  env: StripePriceEnv = {}
): string {
  const viteKey = V2_SUB_ENV_KEYS[plan.id][currency];
  const serverKey = viteKey.replace(/^VITE_/, '');
  return envLookup(env, [viteKey, serverKey]) || plan.defaultStripePriceIds[currency] || '';
}

export function getNewCustomerPacks(): readonly RehubCreditPackDefinitionV2[] {
  return REHUB_CREDIT_PACKS_V2;
}

export function getNewCustomerPlans(): readonly RehubCreditSubscriptionDefinitionV2[] {
  return REHUB_CREDIT_MONTHLY_PLANS_V2;
}

/**
 * Dashboard / embed catalogue for new customers (photoclear, staging, sketchplan).
 * Uses the live v2 plans and PAYG packs — the same figures Hub and marketing show.
 */
export function buildDashboardProductCatalog(env: StripePriceEnv = {}) {
  return {
    SUBSCRIPTIONS: REHUB_CREDIT_MONTHLY_PLANS_V2.map((p) => ({
      id: p.id,
      name: p.label,
      credits: p.credits,
      bonus: 0,
      popular: p.badge === 'popular',
      costPerCredit: {
        USD: `${p.perCredit.USD} per credit`,
        EUR: `${p.perCredit.EUR} per credit`,
        GBP: `${p.perCredit.GBP} per credit`,
      },
      valueLabel: null as string | null,
      thirdFeature: null as string | null,
      prices: {
        USD: {
          id: getSubscriptionStripePriceIdV2(p, 'USD', env),
          amount: minorToMajor(p.amountMinor.USD),
        },
        EUR: {
          id: getSubscriptionStripePriceIdV2(p, 'EUR', env),
          amount: minorToMajor(p.amountMinor.EUR),
        },
        GBP: {
          id: getSubscriptionStripePriceIdV2(p, 'GBP', env),
          amount: minorToMajor(p.amountMinor.GBP),
        },
      },
    })),
    TOPUPS: REHUB_CREDIT_PACKS_V2.map((p) => ({
      id: p.id,
      name: p.label,
      credits: p.credits,
      bonus: 0,
      popular: p.badge === 'popular',
      costPerCredit: {
        USD: `${p.perCredit.USD} per credit`,
        EUR: `${p.perCredit.EUR} per credit`,
        GBP: `${p.perCredit.GBP} per credit`,
      },
      prices: {
        USD: {
          id: getPackStripePriceIdV2(p, 'USD', env),
          amount: minorToMajor(p.amountMinor.USD),
        },
        EUR: {
          id: getPackStripePriceIdV2(p, 'EUR', env),
          amount: minorToMajor(p.amountMinor.EUR),
        },
        GBP: {
          id: getPackStripePriceIdV2(p, 'GBP', env),
          amount: minorToMajor(p.amountMinor.GBP),
        },
      },
    })),
  };
}

export function resolveCreditPackCheckoutAny(
  packId: string,
  currency: CreditCurrency,
  env: StripePriceEnv = {}
): {
  packId: string;
  credits: number;
  amountMinor: number;
  priceId: string;
  pricingVersion: RehubPricingVersion;
  purchaseType: 'credit_pack';
  neverExpires: boolean;
  requiresPriceId: boolean;
} | null {
  const v2 = getCreditPackV2(packId);
  if (v2) {
    const priceId = getPackStripePriceIdV2(v2, currency, env);
    return {
      packId: v2.id,
      credits: v2.credits,
      amountMinor: v2.amountMinor[currency],
      priceId,
      pricingVersion: 'v2',
      purchaseType: 'credit_pack',
      neverExpires: true,
      requiresPriceId: true,
    };
  }
  const v1 = getCreditPack(packId);
  if (!v1) return null;
  return {
    packId: v1.id,
    credits: v1.credits,
    amountMinor: v1.amountMinor[currency],
    priceId: getPackStripePriceId(v1, currency, env),
    pricingVersion: 'v1',
    purchaseType: 'credit_pack',
    neverExpires: false,
    requiresPriceId: false,
  };
}

export function resolveSubscriptionCheckoutAny(
  planId: string,
  currency: CreditCurrency,
  env: StripePriceEnv = {}
): {
  planId: string;
  label: string;
  credits: number;
  amountMinor: number;
  priceId: string;
  pricingVersion: RehubPricingVersion;
  purchaseType: 'credit_subscription';
  rolloverCap: number | null;
} | null {
  const v2 = getCreditSubscriptionV2(planId);
  if (v2) {
    const priceId = getSubscriptionStripePriceIdV2(v2, currency, env);
    if (!priceId) return null;
    return {
      planId: v2.id,
      label: v2.label,
      credits: v2.credits,
      amountMinor: v2.amountMinor[currency],
      priceId,
      pricingVersion: 'v2',
      purchaseType: 'credit_subscription',
      rolloverCap: v2.rolloverCap,
    };
  }
  const v1 = getCreditSubscription(planId);
  if (!v1) return null;
  const priceId = getSubscriptionStripePriceId(v1, currency, env);
  if (!priceId) return null;
  return {
    planId: v1.id,
    label: v1.label,
    credits: v1.credits,
    amountMinor: v1.amountMinor[currency],
    priceId,
    pricingVersion: 'v1',
    purchaseType: 'credit_subscription',
    rolloverCap: null,
  };
}

export type ResolvedSubscriptionGrant = {
  planId: string;
  credits: number;
  label: string;
  pricingVersion: RehubPricingVersion;
  rolloverCap: number | null;
};

/**
 * Price ID always wins so a v1 Starter price still grants 30 credits and a
 * grandfathered pro_v2 / studio_v2 price still grants 150 / 500.
 */
export function buildAllSubscriptionPriceCreditMap(
  env: StripePriceEnv = {}
): Record<string, ResolvedSubscriptionGrant> {
  const map: Record<string, ResolvedSubscriptionGrant> = {};

  for (const [priceId, known] of Object.entries(KNOWN_SUBSCRIPTION_PRICE_TO_PLAN)) {
    map[priceId] = {
      planId: known.planId,
      credits: known.credits,
      label: known.label,
      pricingVersion: 'v1',
      rolloverCap: null,
    };
  }

  for (const plan of REHUB_CREDIT_MONTHLY_PLANS) {
    for (const currency of REHUB_CURRENCIES) {
      const priceId = getSubscriptionStripePriceId(plan, currency, env);
      if (priceId) {
        map[priceId] = {
          planId: plan.id,
          credits: plan.credits,
          label: plan.label,
          pricingVersion: 'v1',
          rolloverCap: null,
        };
      }
    }
  }

  // Grandfathered v2 plans first, current (v3) plans last — the price IDs are
  // distinct, so both generations resolve to their own allowances.
  for (const plan of ALL_V2_WALLET_PLANS) {
    for (const currency of REHUB_CURRENCIES) {
      const priceId = getSubscriptionStripePriceIdV2(plan, currency, env);
      if (priceId) {
        map[priceId] = {
          planId: plan.id,
          credits: plan.credits,
          label: plan.label,
          pricingVersion: 'v2',
          rolloverCap: plan.rolloverCap,
        };
      }
    }
  }

  return map;
}

export function getSubscriptionByStripePriceId(
  priceId: string | null | undefined,
  env: StripePriceEnv = {}
): ResolvedSubscriptionGrant | null {
  if (!priceId) return null;
  return buildAllSubscriptionPriceCreditMap(env)[priceId] ?? null;
}

/**
 * Fulfilment resolver. Stripe Price ID wins over plan_id metadata so legacy
 * subscribers cannot be silently moved onto v2 allowances.
 */
export function resolveSubscriptionGrant(opts: {
  planId?: string | null;
  priceId?: string | null;
  creditsMetadata?: string | number | null;
  env?: StripePriceEnv;
}): ResolvedSubscriptionGrant | null {
  const env = opts.env || {};
  const fromPrice = getSubscriptionByStripePriceId(opts.priceId, env);
  if (fromPrice) return fromPrice;

  const fromPlan = getAnyCreditSubscription(opts.planId);
  if (fromPlan) {
    const isV2 = 'pricingVersion' in fromPlan && fromPlan.pricingVersion === 'v2';
    return {
      planId: fromPlan.id,
      credits: fromPlan.credits,
      label: fromPlan.label,
      pricingVersion: isV2 ? 'v2' : 'v1',
      rolloverCap: isV2 ? (fromPlan as RehubCreditSubscriptionDefinitionV2).rolloverCap : null,
    };
  }

  const raw = opts.creditsMetadata;
  if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
    const n = parseInt(String(raw), 10);
    if (!Number.isNaN(n) && n > 0) {
      return {
        planId: opts.planId || 'unknown',
        credits: n,
        label: 'Credits',
        pricingVersion: 'v1',
        rolloverCap: null,
      };
    }
  }
  return null;
}

export function buildAllPackPriceCreditMap(
  env: StripePriceEnv = {}
): Record<string, { packId: string; credits: number; pricingVersion: RehubPricingVersion; neverExpires: boolean }> {
  const map: Record<string, { packId: string; credits: number; pricingVersion: RehubPricingVersion; neverExpires: boolean }> = {};
  for (const pack of REHUB_CREDIT_PACKS) {
    for (const currency of REHUB_CURRENCIES) {
      const priceId = getPackStripePriceId(pack, currency, env);
      if (priceId) {
        map[priceId] = { packId: pack.id, credits: pack.credits, pricingVersion: 'v1', neverExpires: false };
      }
    }
  }
  for (const pack of REHUB_CREDIT_PACKS_V2) {
    for (const currency of REHUB_CURRENCIES) {
      const priceId = getPackStripePriceIdV2(pack, currency, env);
      if (priceId) {
        map[priceId] = { packId: pack.id, credits: pack.credits, pricingVersion: 'v2', neverExpires: true };
      }
    }
  }
  return map;
}

export function getPackByStripePriceId(
  priceId: string | null | undefined,
  env: StripePriceEnv = {}
) {
  if (!priceId) return null;
  return buildAllPackPriceCreditMap(env)[priceId] ?? null;
}

export function subscriptionRolloverCap(planId: string | null | undefined): number | null {
  return getCreditSubscriptionV2(planId)?.rolloverCap ?? null;
}

/** v2 invoice grant: min(allowance, cap − remaining subscription credits). PAYG is excluded. */
export function computeSubscriptionRolloverGrant(
  monthlyAllowance: number,
  remainingSubscriptionCredits: number,
  rolloverCap: number
): number {
  if (monthlyAllowance <= 0 || rolloverCap <= 0) return 0;
  const room = rolloverCap - Math.max(0, remainingSubscriptionCredits);
  return Math.max(0, Math.min(monthlyAllowance, room));
}

export function isLegacyCreditPlanId(id: string | null | undefined): boolean {
  if (!id) return false;
  return !!getCreditSubscription(id) && !getCreditSubscriptionV2(id);
}

export type CustomerCatalogueMode = 'new' | 'legacy';

export function customerCatalogueMode(
  pricingVersion: RehubPricingVersion | string | null | undefined
): CustomerCatalogueMode {
  return pricingVersion === 'v1' ? 'legacy' : 'new';
}

/** Display packs for the buy-credits UI. Legacy subscribers still see v1 packs. */
export function packsForCustomer(
  pricingVersion: RehubPricingVersion | string | null | undefined
): readonly (RehubCreditPackDefinition | RehubCreditPackDefinitionV2)[] {
  return customerCatalogueMode(pricingVersion) === 'legacy' ? REHUB_CREDIT_PACKS : REHUB_CREDIT_PACKS_V2;
}

export function plansForCustomer(
  pricingVersion: RehubPricingVersion | string | null | undefined
): readonly (RehubCreditSubscriptionDefinition | RehubCreditSubscriptionDefinitionV2)[] {
  return customerCatalogueMode(pricingVersion) === 'legacy'
    ? REHUB_CREDIT_MONTHLY_PLANS
    : REHUB_CREDIT_MONTHLY_PLANS_V2;
}

export function formatV2PerCredit(amountMinor: number, credits: number, currency: CreditCurrency): string {
  return formatPerCreditFromMinor(amountMinor, credits, currency);
}

export const V1_KNOWN_SUBSCRIPTION_PRICE_IDS = Object.keys(KNOWN_SUBSCRIPTION_PRICE_TO_PLAN);
